import { useMount } from '@contra/react-hooks/useMount';
import { type ReactNode, type ReactPortal, useRef } from 'react';
import { createPortal } from 'react-dom';

export type UsePortalOptions<K extends keyof HTMLElementTagNameMap> = {
  /**
   * The element ID to mount the portal too if it is in the DOM.
   */
  id?: string;
  /**
   * The portal key. Used to control portal rendering.
   */
  key?: string;
  /**
   * Type of HTML element to create if mounting new element
   * and not using existing element via `id` option.
   */
  tagName?: K;
};

/**
 * Renders a React node via a portal to either an existing DOM element via `id`
 * or creates a new element and mounts it to the DOM to use for portal rendering.
 * @param children {ReactNode} The children to render in the portal.
 * @param options {UsePortalOptions} The options to use when mounting the portal.
 * @param options.id {string} The element ID to mount the portal too if it is in the DOM.
 * @param options.key {string} The portal key. Used to control portal rendering.
 * @param options.tagName {string} Type of HTML element to create if mounting new element
 * @returns {ReactPortal} The portal to render.
 * @example
 * ```tsx
 * function Component() {
 *   const portal = usePortal(<div>Hello World</div>);
 *
 *   return <>{portal}</>;
 * }
 * ```
 */
export const useMountedPortal = <K extends keyof HTMLElementTagNameMap>(
  children: ReactNode,
  { id, key, tagName }: UsePortalOptions<K> = {},
): null | ReactPortal => {
  const portalElement = useRef<{
    element: HTMLElement | null;
    mounted: boolean;
  }>(
    (() => {
      if (typeof document === 'undefined') {
        return {
          element: null,
          mounted: false,
        };
      }

      if (id) {
        // eslint-disable-next-line unicorn/prefer-query-selector
        const element = document.getElementById(id);

        if (element) {
          return { element, mounted: true };
        }
      }

      return {
        element: document.createElement(tagName ?? 'div'),
        mounted: false,
      };
    })(),
  );

  useMount(() => {
    const { element, mounted } = portalElement.current;

    if (!element || mounted) {
      return () => {};
    }

    document.body.appendChild(element);

    return () => {
      document.body.removeChild(element);
    };
  });

  if (!portalElement.current.element) {
    return null;
  }

  return createPortal(children, portalElement.current.element, key);
};
